import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { api } from '@/lib/api';
import type * as API from '@/types/api';
import { useAccountStore } from '@nimbitlabs/features';

interface IncidentsState {
  incidents: API.IncidentDto[];
  loading: boolean;
  error: string | null;
  
  fetchIncidents: () => Promise<void>;
  addIncident: (incidentData: API.CreateIncidentDto) => Promise<API.IncidentDto>;
  updateIncident: (id: string, incidentData: API.UpdateIncidentDto) => Promise<void>;
  deleteIncident: (incidentId: string) => Promise<void>;
  getIncidentById: (id: string) => Promise<API.IncidentDto>;
  clearError: () => void;
}

const incidentsStore = create<IncidentsState>()(
  subscribeWithSelector((set, get) => ({
  incidents: [],
  loading: false,
  error: null,
  
  fetchIncidents: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.incidents.getAll();
      set({ incidents: data, loading: false });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch incidents', loading: false });
    }
  },
  
  addIncident: async (incidentData: API.CreateIncidentDto) => {
    const tempId = `temp-${Date.now()}`;
    const optimisticIncident: API.IncidentDto = {
      id: tempId,
      ...incidentData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      resolvedAt: null,
    };

    set(state => ({ incidents: [optimisticIncident, ...state.incidents] }));

    try {
      const created = await api.incidents.create(incidentData);
      set(state => ({
        incidents: state.incidents.map(i => i.id === tempId ? created : i)
      }));
      return created;
    } catch (error) {
      set(state => ({
        incidents: state.incidents.filter(i => i.id !== tempId),
        error: error instanceof Error ? error.message : 'Failed to create incident'
      }));
      throw error;
    }
  },
  
  updateIncident: async (id: string, incidentData: API.UpdateIncidentDto) => {
    const original = get().incidents.find(i => i.id === id);
    
    set(state => ({
      incidents: state.incidents.map(i => 
        i.id === id ? { ...i, ...incidentData, updatedAt: new Date().toISOString() } : i
      )
    }));
    
    try {
      const updated = await api.incidents.update(id, incidentData);
      set(state => ({
        incidents: state.incidents.map(i => i.id === id ? updated : i)
      }));
    } catch (error) {
      if (original) {
        set(state => ({
          incidents: state.incidents.map(i => i.id === id ? original : i)
        }));
      }
      throw error;
    }
  },
  
  deleteIncident: async (incidentId: string) => {
    const original = get().incidents;
    
    set(state => ({
      incidents: state.incidents.filter(i => i.id !== incidentId)
    }));
    
    try {
      await api.incidents.delete(incidentId);
    } catch (error) {
      set({ incidents: original });
      throw error;
    }
  },
  
  getIncidentById: async (id: string) => {
    const cached = get().incidents.find(i => i.id === id);
    if (cached) return cached;
    
    const data = await api.incidents.getById(id);
    set(state => ({
      incidents: [...state.incidents.filter(i => i.id !== id), data]
    }));
    return data;
  },
  
  clearError: () => set({ error: null }),
  }))
);

export const useIncidentsStore = incidentsStore;

// Subscribe to account changes and auto-fetch
useAccountStore.subscribe(
  (state) => state.currentAccount,
  (currentAccount) => {
    if (currentAccount?.id) {
      incidentsStore.getState().fetchIncidents();
    } else {
      incidentsStore.setState({ incidents: [] });
    }
  }
);
